import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";

/**
 * Chromecast helper shared by public VOD and live playback surfaces.
 *
 * Ports the legacy `js/player.js` Cast SDK/session manager and
 * `js/live.js:castLiveStream` paths into a React hook. The hook owns:
 *   - one-time Cast Sender SDK loading
 *   - CastContext configuration with the default media receiver
 *   - session start/resume/end state tracking
 *   - media load requests for BUFFERED MP4 VOD or LIVE HLS streams
 *
 * The UI intentionally keeps a regular app-styled button next to the
 * existing AirPlay button instead of relying on the default Cast button
 * custom element. That lets the public game and live shells preserve the
 * existing Tktic control styling while still using the official SDK.
 */

type CastSessionState = "SESSION_STARTED" | "SESSION_RESUMED" | "SESSION_ENDED";

type CastEvent = { sessionState?: CastSessionState | string };

type CastImage = { url: string };

type CastMetadata = {
  title?: string;
  subtitle?: string;
  images?: CastImage[];
};

type CastMediaInfo = {
  contentId: string;
  contentType: string;
  streamType?: string;
  metadata?: CastMetadata;
};

type CastLoadRequest = {
  media: CastMediaInfo;
  currentTime?: number;
  autoplay?: boolean;
};

type CastDevice = { friendlyName?: string };

type CastSession = {
  getCastDevice: () => CastDevice;
  loadMedia: (request: CastLoadRequest) => Promise<unknown>;
};

type CastContext = {
  setOptions: (options: { receiverApplicationId: string; autoJoinPolicy: string }) => void;
  addEventListener: (type: string, listener: (event: CastEvent) => void) => void;
  removeEventListener?: (type: string, listener: (event: CastEvent) => void) => void;
  requestSession: () => Promise<unknown>;
  endCurrentSession: (stopCasting: boolean) => void;
  getCurrentSession: () => CastSession | null;
};

type CastFramework = {
  CastContext: { getInstance: () => CastContext };
  CastContextEventType: { SESSION_STATE_CHANGED: string };
  SessionState: Record<string, CastSessionState>;
};

type ChromeCastApi = {
  AutoJoinPolicy: { ORIGIN_SCOPED: string };
  media: {
    DEFAULT_MEDIA_RECEIVER_APP_ID: string;
    StreamType: { BUFFERED: string; LIVE: string };
    MediaInfo: new (contentId: string, contentType: string) => CastMediaInfo;
    GenericMediaMetadata: new () => CastMetadata;
    LoadRequest: new (media: CastMediaInfo) => CastLoadRequest;
  };
};

type CastWindow = Window & {
  cast?: { framework?: CastFramework };
  chrome?: { cast?: ChromeCastApi };
  __onGCastApiAvailable?: (isAvailable: boolean) => void;
  __tkticCastSdkPromise?: Promise<boolean>;
};

export type ChromecastMedia = {
  url: string;
  contentType: "video/mp4" | "application/x-mpegURL";
  streamType: "BUFFERED" | "LIVE";
  title: string;
  subtitle?: string;
  imageUrl?: string | null;
  currentTime?: number;
};

export type ChromecastState = {
  supportedBrowser: boolean;
  secureContextAllowed: boolean;
  sdkReady: boolean;
  casting: boolean;
  deviceName: string | null;
  unavailableReason: string | null;
  toggle: () => Promise<void>;
};

function castWindow(): CastWindow {
  return window as CastWindow;
}

function isIosDevice(): boolean {
  const ua = navigator.userAgent || "";
  // iPhone/iPad/iPod report their type directly. iPadOS 13+ masquerades
  // as desktop Safari ("Macintosh"), so fall back to the touch-point
  // check that distinguishes a real Mac (0) from a touch iPad (>1).
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function isCastSupportedBrowser(): boolean {
  const ua = navigator.userAgent || "";
  // Google's web Cast Sender SDK only works in desktop Chromium-based
  // browsers. Every iOS browser — including Chrome for iOS (CriOS) and
  // Edge for iOS (EdgiOS) — is a WebKit skin that can't load the SDK,
  // so exclude iOS outright. Those users get AirPlay instead.
  if (isIosDevice()) return false;
  return /(Chrome|Chromium|CriOS|Edg)\//.test(ua) && !/OPR\//.test(ua);
}

function isCastSecureContextAllowed(): boolean {
  if (window.isSecureContext) return true;
  const hostname = window.location.hostname || "";
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

function hasCastFramework(): boolean {
  const w = castWindow();
  return !!w.cast?.framework && !!w.chrome?.cast?.media;
}

function loadCastSdk(): Promise<boolean> {
  const w = castWindow();
  if (hasCastFramework()) return Promise.resolve(true);
  if (w.__tkticCastSdkPromise) return w.__tkticCastSdkPromise;

  w.__tkticCastSdkPromise = new Promise<boolean>((resolve) => {
    const previous = w.__onGCastApiAvailable;
    let settled = false;
    const finish = (available: boolean) => {
      if (settled) return;
      settled = true;
      resolve(available && hasCastFramework());
    };

    w.__onGCastApiAvailable = (isAvailable: boolean) => {
      previous?.(isAvailable);
      finish(isAvailable);
    };

    const existing = document.querySelector<HTMLScriptElement>("script[data-tktic-cast-sdk]");
    if (!existing) {
      const script = document.createElement("script");
      script.src = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
      script.async = true;
      script.dataset.tkticCastSdk = "true";
      script.onerror = () => finish(false);
      document.head.appendChild(script);
    }

    window.setTimeout(() => finish(hasCastFramework()), 1500);
  });
  return w.__tkticCastSdkPromise;
}

function absoluteUrl(url: string): string {
  return url.startsWith("http://") || url.startsWith("https://")
    ? url
    : new URL(url, window.location.origin).toString();
}

function configureCastContext(): CastContext | null {
  const w = castWindow();
  const framework = w.cast?.framework;
  const chromeCast = w.chrome?.cast;
  if (!framework || !chromeCast?.media) return null;
  const context = framework.CastContext.getInstance();
  context.setOptions({
    receiverApplicationId: chromeCast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
    autoJoinPolicy: chromeCast.AutoJoinPolicy.ORIGIN_SCOPED,
  });
  return context;
}

function buildLoadRequest(media: ChromecastMedia): CastLoadRequest | null {
  const chromeCast = castWindow().chrome?.cast;
  if (!chromeCast?.media) return null;

  const mediaInfo = new chromeCast.media.MediaInfo(
    absoluteUrl(media.url),
    media.contentType,
  );
  mediaInfo.streamType = chromeCast.media.StreamType[media.streamType];

  const metadata = new chromeCast.media.GenericMediaMetadata();
  metadata.title = media.title;
  metadata.subtitle = media.subtitle;
  if (media.imageUrl) {
    metadata.images = [{ url: absoluteUrl(media.imageUrl) }];
  }
  mediaInfo.metadata = metadata;

  const request = new chromeCast.media.LoadRequest(mediaInfo);
  request.autoplay = true;
  if (media.streamType === "BUFFERED") {
    request.currentTime = media.currentTime || 0;
  }
  return request;
}

export function useChromecast(
  videoRef: RefObject<HTMLVideoElement | null>,
  mediaFactory: () => ChromecastMedia,
  options: { onConnected?: () => void; onDisconnected?: () => void } = {},
): ChromecastState {
  const supportedBrowser = useMemo(isCastSupportedBrowser, []);
  const secureContextAllowed = useMemo(isCastSecureContextAllowed, []);
  const [sdkReady, setSdkReady] = useState(hasCastFramework);
  const [casting, setCasting] = useState(false);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const contextRef = useRef<CastContext | null>(null);
  const sessionEventHandledRef = useRef(false);
  const mediaFactoryRef = useRef(mediaFactory);
  const optionsRef = useRef(options);

  useEffect(() => {
    mediaFactoryRef.current = mediaFactory;
    optionsRef.current = options;
  }, [mediaFactory, options]);

  useEffect(() => {
    if (!supportedBrowser || !secureContextAllowed) return;
    let cancelled = false;
    let removeSessionListener: (() => void) | null = null;

    const loadSessionMedia = (session: CastSession) => {
      setCasting(true);
      setDeviceName(session.getCastDevice().friendlyName || "TV");
      const request = buildLoadRequest(mediaFactoryRef.current());
      if (request) {
        void session.loadMedia(request).then(() => {
          videoRef.current?.pause();
          optionsRef.current.onConnected?.();
        });
      }
    };

    const onSessionStateChanged = (event: CastEvent) => {
      const framework = castWindow().cast?.framework;
      const ended = framework?.SessionState?.SESSION_ENDED || "SESSION_ENDED";
      const started = framework?.SessionState?.SESSION_STARTED || "SESSION_STARTED";
      const resumed = framework?.SessionState?.SESSION_RESUMED || "SESSION_RESUMED";

      if (event.sessionState === ended) {
        setCasting(false);
        setDeviceName(null);
        optionsRef.current.onDisconnected?.();
        return;
      }
      if (event.sessionState === started || event.sessionState === resumed) {
        const session = contextRef.current?.getCurrentSession() || null;
        if (session) {
          sessionEventHandledRef.current = true;
          loadSessionMedia(session);
        }
      }
    };

    loadCastSdk().then((ready) => {
      if (cancelled) return;
      setSdkReady(ready);
      if (!ready) return;
      const context = configureCastContext();
      contextRef.current = context;
      if (!context) return;
      const eventType = castWindow().cast?.framework?.CastContextEventType?.SESSION_STATE_CHANGED;
      if (eventType) {
        context.addEventListener(eventType, onSessionStateChanged);
        removeSessionListener = () => context.removeEventListener?.(eventType, onSessionStateChanged);
      }
      const session = context.getCurrentSession();
      if (session) {
        setCasting(true);
        setDeviceName(session.getCastDevice().friendlyName || "TV");
      }
    });

    return () => {
      cancelled = true;
      removeSessionListener?.();
    };
  }, [secureContextAllowed, supportedBrowser, videoRef]);

  const toggle = useCallback(async () => {
    if (!supportedBrowser || !secureContextAllowed) return;
    const context = contextRef.current || configureCastContext();
    contextRef.current = context;
    if (!context) return;
    const current = context.getCurrentSession();
    if (current) {
      context.endCurrentSession(true);
      return;
    }
    try {
      sessionEventHandledRef.current = false;
      await context.requestSession();
      const session = context.getCurrentSession();
      if (session && !sessionEventHandledRef.current) {
        setCasting(true);
        setDeviceName(session.getCastDevice().friendlyName || "TV");
        const request = buildLoadRequest(mediaFactoryRef.current());
        if (request) {
          await session.loadMedia(request);
          videoRef.current?.pause();
          optionsRef.current.onConnected?.();
        }
      }
    } catch {
      // User cancelled the device picker or no device was available.
    }
  }, [secureContextAllowed, supportedBrowser]);

  let unavailableReason: string | null = null;
  if (!supportedBrowser) unavailableReason = "Chromecast is available in Chrome or Edge.";
  else if (!secureContextAllowed) unavailableReason = "Chromecast requires HTTPS or localhost.";
  else if (!sdkReady) unavailableReason = "Looking for Chromecast devices…";

  return {
    supportedBrowser,
    secureContextAllowed,
    sdkReady,
    casting,
    deviceName,
    unavailableReason,
    toggle,
  };
}
